import * as React from "react";
import styled from "@emotion/styled";
import ReactCrop, { Crop, PixelCrop, PercentCrop } from "react-image-crop";
import imageUploadSrc from "assets/images/image-upload.svg";
import { Button, Callout, FlexRow, Img, Text, Error } from "atoms";
import { canvasPreview } from "../utils/canvasPreview";
import {
  ASPECT_RATIO_HERO,
  ASPECT_RATIO_BANNER,
  centerCrop,
} from "../utils/centerCrop";
import "react-image-crop/dist/ReactCrop.css";
import { formatSize } from "../utils/formatSize";

const DEFAULT_MAX_FILE_SIZE = 10000000;

const stopDragEvent: React.DragEventHandler = (event) => {
  event.stopPropagation();
  event.preventDefault();
};

type ImageCropperProps = {
  onCancel?: () => void;
  onSubmit: (srcFile: File, cropBlob: Blob) => void;
  small?: boolean;
  maxFileSize?: number;
  banner?: boolean;
};

function ImageCropper({
  onCancel,
  onSubmit,
  small = false,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  banner = false,
}: ImageCropperProps) {
  const [src, setSrc] = React.useState("");
  const [srcFile, setSrcFile] = React.useState<File>();
  const [crop, setCrop] = React.useState<Crop>();
  const [complete, setComplete] = React.useState<PixelCrop>();
  const [isDragging, setIsDragging] = React.useState(false);
  const [isImageTooLarge, setIsImageTooLarge] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const imgRef = React.useRef<HTMLImageElement>(null);

  // When completed crop changes, update a preview canvas element with cropped result
  React.useEffect(() => {
    const updatePreview = async () => {
      if (
        complete?.width &&
        complete?.height &&
        imgRef.current &&
        canvasRef.current
      ) {
        canvasPreview(imgRef.current, canvasRef.current, complete, 1, 0);
      }
    };

    // Debounce the effect so we don't redraw the canvas on every pixel change
    const timeout = setTimeout(updatePreview, 200);
    return () => clearTimeout(timeout);
  }, [complete]);

  /**
   * Initialize cropper with new file from select or drop event's file
   */
  const handleFile = (file: File) => {
    // Reset cropped file, if exists
    setCrop(undefined);

    // Read selected file and update state
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (file.size <= maxFileSize) {
        setSrc(reader.result?.toString() ?? "");
        setSrcFile(file);
      } else {
        setIsImageTooLarge(true);
      }
    });
    reader.readAsDataURL(file);
  };

  /**
   * Handle onChange event when selecting file through dialog menu
   */
  const handleSelectFile: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFile(event.target.files[0]);
    }
  };

  /**
   * Update state to style on start of dragging event
   */
  const handleDragEnter: React.DragEventHandler = (event) => {
    stopDragEvent(event);
    setIsDragging(true);
  };

  /**
   * Update state to style on end of dragging event
   */
  const handleDragLeave: React.DragEventHandler = () => {
    setIsDragging(false);
  };

  /**
   * Handle onDrop event when selecting file through drag and drop
   */
  const handleDrop: React.DragEventHandler = (event) => {
    stopDragEvent(event);
    const files = event.dataTransfer?.files;
    if (files) handleFile(files[0]);
  };

  /**
   * Set default crop once image is selected
   */
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = event.currentTarget;
    setCrop(centerCrop(width, height, banner));
  };

  /**
   * Update crop dimensions on resize and move actions
   */
  const handleCrop = (_: PixelCrop, percent: PercentCrop) => {
    setCrop(percent);
  };
  const handleComplete = (pixel: PixelCrop) => setComplete(pixel);

  /**
   * Reset cropper to initial state with empty file input
   */
  const handleReset = () => {
    setSrc("");
    setSrcFile(undefined);
    setCrop(undefined);
    setComplete(undefined);
    setIsDragging(false);
    const element = inputRef.current;
    if (element) element.value = "";
  };

  const handleSubmit: React.MouseEventHandler = () => {
    canvasRef.current?.toBlob((blob) => {
      if (srcFile instanceof File && blob instanceof Blob) {
        onSubmit(srcFile, blob);
      }
    });
  };

  return (
    <SCallout small={small}>
      {!src && (
        <Dropbox
          isDragging={isDragging}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={stopDragEvent}
          onDrop={handleDrop}
          small={small}
        >
          <div>
            {!small && <SImage src={imageUploadSrc} alt="" />}
            <Text bold size={small ? "sm" : "lg"}>
              Drop file to upload
            </Text>
          </div>

          <div>
            <Text size="xs">or browse to choose a file</Text>
            <SButton variant="primary" as="label" small>
              Browse Files...
              <HiddenInput
                type="file"
                accept="image/*"
                onChange={handleSelectFile}
                ref={inputRef}
              />
            </SButton>
          </div>

          <div>
            <Text size="xxs">
              JPG, PNG, and GIF files are supported. {!small && <br />}
              Max file size is {formatSize(maxFileSize)}.
            </Text>
          </div>

          {isImageTooLarge && (
            <Error size="sm" role="alert">
              The selected image exceeds the maximum size.
            </Error>
          )}

          {onCancel && small ? (
            <SButton variant="blank" onClick={onCancel} small>
              Cancel
            </SButton>
          ) : (
            <SButton variant="primary" onClick={onCancel} small>
              Cancel
            </SButton>
          )}
        </Dropbox>
      )}

      {src && (
        <CropWrapper>
          <Text size="lg" bold>
            Crop and Center Image
          </Text>

          <ReactCrop
            aspect={banner ? ASPECT_RATIO_BANNER : ASPECT_RATIO_HERO}
            crop={crop}
            onChange={handleCrop}
            onComplete={handleComplete}
            keepSelection
          >
            <Img
              ref={imgRef}
              alt="Crop your uploaded file"
              src={src}
              onLoad={handleImageLoad}
            />
          </ReactCrop>

          <FlexRow justifyContent="space-between">
            <SButton variant="primary" onClick={handleReset} small={small}>
              Cancel
            </SButton>

            <SButton variant="gradient" onClick={handleSubmit} small={small}>
              Crop Image
            </SButton>
          </FlexRow>
        </CropWrapper>
      )}

      {complete && <Canvas ref={canvasRef} />}
    </SCallout>
  );
}

type WithSmall = { small?: boolean };

const options = {
  shouldForwardProp: (prop: string) => prop !== "small",
} as const;

const SCallout = styled(
  Callout,
  options
)<WithSmall>(({ small, theme }) => ({
  ...(small && {
    padding: 10,
    display: "inline-block",
    maxWidth: "100%",
    "@media (min-width: 600px)": {
      padding: 10,
    },
  }),
  ".ReactCrop__crop-selection": {
    border: `2px dashed ${theme.colors.primary}`,
  },
}));

const flexColumn = {
  alignItems: "center",
  display: "flex",
  gap: "2rem",
  flexDirection: "column",
  justifyContent: "space-around",
  textAlign: "center",
} as const;

type WithIsDragging = {
  isDragging: boolean;
};

const Dropbox = styled.div<WithIsDragging & WithSmall>(
  ({ small, theme, isDragging }) => ({
    border: `2px dashed ${
      isDragging ? theme.colors.black : theme.colors.primary
    }`,
    borderRadius: theme.borderRadius.md,
    padding: "2rem 1rem",
    transition: "border ease 0.3s",
    ...flexColumn,
    "& > div": {
      width: "max-content",
      maxWidth: "100%",
    },
    "& p": {
      marginBottom: "0.5rem",
    },
    "& p:last-child": {
      marginBottom: "0",
    },
    ...(small && {
      gap: ".66rem",
      padding: "1.5rem 1rem",
    }),
  })
);

const CropWrapper = styled.div({
  ...flexColumn,
  margin: "0 auto",
  maxWidth: 600,
  "& p": { marginBottom: 0 },
  "& > div:last-child": {
    width: "100%",
  },
});

const SImage = styled(Img)({
  maxWidth: 200,
  width: "25vw",
});

const SButton = styled(
  Button,
  options
)<WithSmall>(({ small, variant, theme }) => ({
  ...(variant === "primary" && {
    background: theme.colors.bg,
  }),
  ...(small && {
    fontSize: theme.fontSizes.sm,
  }),
}));

const hiddenStyles = {
  clip: "rect(1px, 1px, 1px, 1px)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  width: 1,
} as const;
const Canvas = styled.canvas(hiddenStyles);
const HiddenInput = styled.input(hiddenStyles);

export { ImageCropper, HiddenInput };
